import { getProductUserData } from '../../api/product';
import InfoBox from '../../generic/InfoBox';
import useUser from '../../login/userContext';
import ProductTools from '../../product-tools/ProductTools';
import useAsyncMemo from '../../utils/useAsyncMemo';
import ProductAvailability from '../ProductAvailability';
import ProductEmailReminder from '../emailreminder/ProductEmailReminder';
import Expired from '../parts/Expired';
import { ProductCategories } from '../parts/ProductCategories';
import ProductRegion from '../parts/ProductRegion';
import PriceAndBuy from './PriceAndBuy';
import ProductDetailsImage from './ProductDetailsImage';
import type { DetailedProduct } from './ProductDetailsPage';
import * as style from './ProductImageAndMainInfo.module.css';

export interface Props {
    product: DetailedProduct;
}

export default function ProductImageAndMainInfo({ product }: Props) {
    const { isLoggedIn } = useUser();
    const [userData, , , , setUserData] = useAsyncMemo(
        (code, isLoggedIn) => (isLoggedIn ? getProductUserData(code) : Promise.resolve(undefined)),
        [product.code, isLoggedIn],
        undefined,
    );
    return (
        <div className={style.productImageAndMainInfo}>
            <ProductTools product={product} productUserData={userData} onUserDataChange={setUserData} />
            <ProductDetailsImage product={product} />
            <div className={style.mainInfo}>
                <div className={style.titleAndCategory}>
                    <ProductCategories product={product} links />
                    <h1 className={style.productTitle}>{product.name}</h1>
                    <ProductRegion product={product} />
                </div>
                <div className={style.clamped}>
                    {product.expired ? (
                        <Expired />
                    ) : (
                        <>
                            <PriceAndBuy product={product} />
                            <hr />
                            <ProductAvailability product={product} forceRefresh large />
                            <hr />
                            <ProductEmailReminder
                                product={product}
                                statusNotificationRegistered={userData?.statusNotificationRegistered}
                                onUserDataChange={setUserData}
                            />
                            {!!product.maxQuantity && (
                                <InfoBox
                                    message={`Dette produktet er kvotert. Du har kun mulighet til å bestille ${product.maxQuantity} flaske(r)`}
                                />
                            )}
                        </>
                    )}
                    {!!product.taste && <p>{product.taste}</p>}
                    <p className={style.traits}>
                        {product.content?.traits?.map((trait) => (
                            <span key={trait.name} aria-label={trait.readableValue}>
                                <strong>{trait.name}</strong> {trait.formattedValue}
                            </span>
                        ))}
                    </p>
                </div>
            </div>
        </div>
    );
}
